import React, { useEffect, useRef } from "react";
import { assetUrl } from "../../lib/api";

const PHOTOS = [
  { src: "/aerials/aer-homes.jpg", alt: "Established homes wrapping the ponds at Deer Park Estates" },
  { src: "/api/assets/c94c9cb8-c9a7-4657-94ae-11157b68450a", alt: "Streetscape of custom homes on a Deer Park Estates street, Spruce Grove" },
  { src: "/aerials/aer-ponds.jpg", alt: "Naturalized ponds and green corridors from above" },
  { src: "/api/assets/389d4013-5c18-44c6-888b-ee4612d9c51f", alt: "Couple walking a community pathway at Deer Park Estates, Spruce Grove" },
  { src: "/aerials/pond-pano.jpg", alt: "Panorama of the ponds at the heart of the community" },
  { src: "/api/assets/24fd3e1b-538e-4a13-8724-b3302c332520", alt: "Aerial view of custom homes and backyards at Deer Park Estates" },
  { src: "/aerials/aer-lots.jpg", alt: "New lots taking shape at Deer Park Estates" },
  { src: "/api/assets/a951c1ac-b322-47d7-8953-f5991ac6cc35", alt: "Father and daughter flying a kite by the pond at Deer Park Estates" },
  { src: "/aerials/trail.jpg", alt: "Tree-lined walking trail through the community" },
  { src: "/aerials/home.jpg", alt: "Custom home by a trusted Deer Park builder" },
];

// Auto-scrolling photo strip that is fully mouse/touch controllable.
// Auto-scroll pauses on hover / while dragging; users can drag or use the
// trackpad/scrollbar to move back and forth. The list is duplicated so the
// scroll wraps seamlessly in both directions. `onOpen` makes tiles clickable.
export const PhotoMarquee = ({ photos, onOpen, speed = 40, height }) => {
  const list = (photos && photos.length ? photos : PHOTOS).map((p) => ({
    src: p.src || p.image,
    alt: p.alt || p.title || "Community photo",
    caption: p.caption,
    raw: p,
  }));
  const strip = [...list, ...list];
  const h = height || "h-[180px] md:h-[260px]";

  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const drag = useRef({ active: false, startX: 0, startLeft: 0, moved: false });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    let last = performance.now();
    const half = () => el.scrollWidth / 2;
    const wrap = () => {
      const hw = half();
      if (hw <= 0) return;
      if (el.scrollLeft >= hw) el.scrollLeft -= hw;
      else if (el.scrollLeft <= 0) el.scrollLeft += hw;
    };
    const tick = (now) => {
      const dt = now - last;
      last = now;
      if (!pausedRef.current && !drag.current.active && !reduced) {
        el.scrollLeft += (speed * dt) / 1000;
        wrap();
      }
      raf = requestAnimationFrame(tick);
    };
    el.addEventListener("scroll", wrap, { passive: true });
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", wrap);
    };
  }, [speed]);

  const onPointerDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    drag.current = { active: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false, captured: false, pointerId: e.pointerId };
    pausedRef.current = true;
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const el = scrollRef.current;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) {
      drag.current.moved = true;
      if (!drag.current.captured) {
        el.setPointerCapture?.(e.pointerId);
        drag.current.captured = true;
      }
    }
    el.scrollLeft = drag.current.startLeft - dx;
  };
  const endDrag = (e) => {
    const el = scrollRef.current;
    if (drag.current.captured) el?.releasePointerCapture?.(e.pointerId);
    drag.current.active = false;
    // keep paused while pointer is still over the strip; onPointerLeave resumes
  };

  return (
    <section className="bg-[#0C1510] border-y border-[#ffffff10] py-6 overflow-hidden" data-testid="photo-marquee">
      <div className="group relative">
        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-4 overflow-x-auto hide-scrollbar cursor-grab active:cursor-grabbing select-none"
          onPointerEnter={() => { pausedRef.current = true; }}
          onPointerLeave={() => { pausedRef.current = false; drag.current.active = false; }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          data-testid="photo-marquee-scroll"
        >
          {strip.map((p, i) => {
            const inner = (
              <>
                <img src={assetUrl(p.src)} alt={p.alt} loading="lazy" draggable={false} className="w-full h-full object-cover pointer-events-none" />
                {onOpen && (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0C1510]/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <p className="absolute bottom-3 left-3 right-3 text-[#F3EEE3] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity text-left">{p.caption || p.alt}</p>
                  </>
                )}
              </>
            );
            const cls = `relative shrink-0 ${h} w-[300px] md:w-[440px] rounded-xl overflow-hidden`;
            return onOpen ? (
              <button
                key={i}
                type="button"
                onClick={() => { if (!drag.current.moved) onOpen(p.raw); }}
                data-testid={`marquee-photo-${i}`}
                className={`${cls} cursor-pointer`}
              >
                {inner}
              </button>
            ) : (
              <div key={i} className={cls}>{inner}</div>
            );
          })}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0C1510] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0C1510] to-transparent" />
      </div>
    </section>
  );
};

export default PhotoMarquee;
